(() => {
  // ring animation controller
  const ringAnimCtrl = { rafId: null, token: 0 };

  function stopRingAnimation() {
    if (ringAnimCtrl.rafId) cancelAnimationFrame(ringAnimCtrl.rafId);
    ringAnimCtrl.rafId = null;
    // bump token so any in-flight loops stop themselves
    ringAnimCtrl.token++;
  }

  function resetRingVisual() {
    const progressContainer = document.querySelector(
      ".featured-circular-progress"
    );
    const circle = progressContainer?.querySelector(".circle");
    const remainingText = document.getElementById("deviceUsageText");
    if (circle) circle.style.strokeDashoffset = 100;
    if (remainingText) remainingText.textContent = "—/—";
  }

  // --- MOCK SETUP ---
  // Parse "YYYY/MM/DD" or return NaN for "未使用"
  function parseJPDate(str) {
    if (!str || str === "未使用") return NaN;
    // "2025/11/05" -> "2025-11-05" so Date can parse reliably
    const isoish = str.replace(/\//g, "-");
    return new Date(isoish).getTime();
  }

  // Choose an item by expiry. mode = "latest" (max future) or "nearest" (soonest)
  function pickByExpiry(list, mode = "latest") {
    if (!Array.isArray(list) || list.length === 0) return null;

    // Decide how to treat "未使用"
    // If you want "未使用" to count as very far future, give it +Infinity
    // If you want to ignore it, give it -Infinity so real dates win.
    const score = (item) => {
      const t = parseJPDate(item.effectiveDate);
      if (Number.isNaN(t)) return Number.POSITIVE_INFINITY; // treat 未使用 as far future
      return t;
    };

    return list.reduce((best, cur) => {
      if (!best) return cur;
      const tb = score(best);
      const tc = score(cur);
      return mode === "latest" ? (tc > tb ? cur : best) : tc < tb ? cur : best;
    }, null);
  }

  // One function to truly “set the active tab”
  function setActiveTab(tabKey, lists) {
    // tab buttons + panes
    const tabs = document.querySelectorAll(".tab-btn");
    const tabContents = document.querySelectorAll(".tab-content");

    tabs.forEach((t) => {
      t.classList.toggle("active", t.dataset.tab === tabKey);
    });

    tabContents.forEach((c) => {
      c.classList.toggle("hidden", c.id !== `tab-${tabKey}`);
    });

    // always stop any running ring animation on tab change
    stopRingAnimation();

    // update the label under the ring
    const planLabelEl = document.querySelector(".device-plan-wrapper p");
    if (planLabelEl) {
      planLabelEl.textContent =
        tabKey === "japan"
          ? "残量(国内)"
          : tabKey === "abroad"
            ? "残量(海外)"
            : "—"; // or 使用済み残量
    }

    if (tabKey === "used") {
      applyDashesToOverlayAndCircle();
      return;
    }

    const regionItem =
      tabKey === "japan" ? lists._selectedJapan : lists._selectedAbroad;
    applyItemToOverlayAndCircle(regionItem);
  }

  // Helper function to force "-" in the overlay
  function applyDashesToOverlayAndCircle() {
    stopRingAnimation();
    const planEl = document.querySelector(".plan-name span:last-child");
    const expiryEl = document.querySelector(".expiry-text span:last-child");
    const remainingText = document.getElementById("deviceUsageText");
    const progressContainer = document.querySelector(
      ".featured-circular-progress"
    );
    const circle = progressContainer?.querySelector(".circle");

    if (planEl) planEl.textContent = "—";
    if (expiryEl) expiryEl.textContent = "—";
    if (remainingText) remainingText.textContent = "—/—";
    if (circle) circle.style.strokeDashoffset = 100;
    resetRingVisual();
  }

  // Keep overlay + circle in sync with one selected item
  function applyItemToOverlayAndCircle(item) {
    if (!item) {
      applyDashesToOverlayAndCircle();
      return;
    }

    // Overlay texts
    const planEl = document.querySelector(".plan-name span:last-child");
    const expiryEl = document.querySelector(".expiry-text span:last-child");
    if (planEl) planEl.textContent = item.plan ?? "—";
    if (expiryEl) expiryEl.textContent = item.effectiveDate ?? "—";

    // Circle uses REMAINING out of TOTAL so the percent matches "残量"
    const total = Number(item.dataTotal) || 0;
    const remaining = Number(item.surplusFlowbyte) || 0;

    // Update the large numeric text (e.g., "20.00 / 50GB")
    const remainingText = document.getElementById("deviceUsageText");
    if (remainingText) {
      remainingText.textContent = `${remaining.toFixed(2)} / ${total}GB`;
    }

    // Drive the progress ring. (animateCircleProgress expects "used / max",
    const progressContainer = document.querySelector(
      ".featured-circular-progress"
    );
    if (progressContainer) {
      animateCircleProgress({
        used: remaining, // treat "used" param as "remaining" for visual
        max: total,
        element: progressContainer,
      });
    }
  }

  // Per-device seeds so each serial is consistent
  const deviceSeeds = new Map();
  function hashSeed(str) {
    // FNV-1a 32-bit
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  // --- Seeded RNG so demos are consistent ---
  function mulberry32(seed) {
    let t = seed >>> 0;
    return function () {
      t += 0x6d2b79f5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }
  function seededPick(arr, rnd) {
    return arr[Math.floor(rnd() * arr.length)];
  }

  const STATUS = {
    INUSE: "利用中",
    NOTACTIVE: "待機中",
    USED: "消費済み",
    EXPIRED: "有効期限切れ",
  };

  // ---- Sorting helpers & state ----
  const SORT_STATUS_ORDER = {
    [STATUS.EXPIRED]: 0, // 有効期限切れ
    [STATUS.USED]: 1, // 消費済み
    [STATUS.NOTACTIVE]: 2, // 待機中
    [STATUS.INUSE]: 3, // 利用中
    不明: -1,
  };

  // compute the *displayed* status for a row given the selectedItem
  function displayStatus(item, selectedItem, tabKey) {
    if (!item) return "不明";

    // If this tab is NOT the active region, force all actives to 待機中
    if (activeRegion !== tabKey && item.status === STATUS.INUSE) {
      return STATUS.NOTACTIVE;
    }

    // In the active region, only the selectedItem remains ACTIVE
    if (
      activeRegion === tabKey &&
      selectedItem &&
      selectedItem.status === STATUS.INUSE &&
      item.status === STATUS.INUSE &&
      item !== selectedItem
    ) {
      return STATUS.NOTACTIVE;
    }

    // return item.status || "不明";
    // Map English status to Japanese
    const statusEn = item.status;
    const statusJp = STATUS[statusEn] || item.status || "不明";
    return statusJp || "不明";
  }

  // global sort state per tab
  const sortState = {
    japan: { key: "effectiveDate", dir: "asc" },
    abroad: { key: "effectiveDate", dir: "asc" },
    used: { key: "effectiveDate", dir: "asc" },
  };

  // normalize a field for sorting
  function sortValue(item, key, selectedItem) {
    switch (key) {
      case "status": {
        const s = displayStatus(item, selectedItem);
        return SORT_STATUS_ORDER[s] ?? -1;
      }
      case "countryName":
        return item.countryName || "";
      case "plan":
        return item.plan || "";
      case "dataTotal":
        return Number(item.dataTotal) || 0;
      case "surplusFlowbyte":
        return Number(item.surplusFlowbyte) || 0;
      case "effectiveDate": {
        const t = parseJPDate(item.effectiveDate);
        return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t; // 未使用 → bottom
      }
      default:
        return "";
    }
  }

  function sortList(list, selectedItem, key, dir) {
    if (!key) return list;
    const mul = dir === "desc" ? -1 : 1;
    return [...list].sort((a, b) => {
      const va = sortValue(a, key, selectedItem);
      const vb = sortValue(b, key, selectedItem);
      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return 0;
    });
  }

  // Map labels for the dropdown
  const SORT_OPTIONS = [
    { key: null, dir: "asc", label: "フィルター (デフォルト)" }, // no sort
    { key: "effectiveDate", dir: "asc", label: "有効期限 ↑（早い順）" },
    { key: "effectiveDate", dir: "desc", label: "有効期限 ↓（遅い順）" },
    { key: "surplusFlowbyte", dir: "desc", label: "残り容量 ↓（多い順）" },
    { key: "surplusFlowbyte", dir: "asc", label: "残り容量 ↑（少ない順）" },
    { key: "dataTotal", dir: "desc", label: "合計容量 ↓（大きい順）" },
    { key: "dataTotal", dir: "asc", label: "合計容量 ↑（小さい順）" },
    { key: "status", dir: "asc", label: "状態 ↑" },
    { key: "status", dir: "desc", label: "状態 ↓" },
    { key: "plan", dir: "asc", label: "プラン ↑（A→Z）" },
    { key: "plan", dir: "desc", label: "プラン ↓（Z→A）" },
    { key: "countryName", dir: "asc",  label: "国名 ↑（A→Z）" },
    { key: "countryName", dir: "desc", label: "国名 ↓（Z→A）" },
  ];

  function optionValue(key, dir) {
    return key ? `${key}:${dir}` : "default";
  }

  function parseOptionValue(v) {
    if (v === "default") return { key: null, dir: "asc" };
    const [key, dir] = v.split(":");
    return { key, dir };
  }

  const ACTIVE_ONLY = [STATUS.INUSE];
  const NON_ACTIVE = [STATUS.USED, STATUS.NOTACTIVE, STATUS.EXPIRED];
  const PLANS = ["365日100GB", "365日5GB", "30日3GB", "7日5GB"];
  const TOTALS = [5, 10, 30, 50, 100];

  function filterByStatus(list, allowed) {
    return Array.isArray(list)
      ? list.filter((i) => allowed.includes(i.status))
      : [];
  }

  function createEntry(rnd, status) {
    const dataTotal = TOTALS[Math.floor(rnd() * TOTALS.length)];

    // Tweak surplus to fit status
    let surplus;
    switch (status) {
      case STATUS.INUSE:
        surplus = +Math.max(dataTotal * (0.25 + rnd() * 0.7), 0.5).toFixed(2);
        break;
      case STATUS.NOTACTIVE:
        surplus = +dataTotal.toFixed(2);
        break;
      case STATUS.USED:
        surplus = +(dataTotal * (0.0 + rnd() * 0.1)).toFixed(2);
        break;
      case STATUS.EXPIRED:
      default:
        surplus = +(dataTotal * (rnd() * 0.05)).toFixed(2);
    }

    // Dates that look legit by status
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0].replace(/-/g, "/");

    let effectiveDate;
    if (status === STATUS.NOTACTIVE) {
      effectiveDate = "未使用";
    } else if (status === STATUS.EXPIRED) {
      const past = new Date(today);
      past.setDate(today.getDate() - Math.floor(5 + rnd() * 30));
      effectiveDate = fmt(past);
    } else {
      const future = new Date(today);
      future.setDate(today.getDate() + Math.floor(5 + rnd() * 90));
      effectiveDate = fmt(future);
    }

    return {
      countryName: "日本",
      status,
      plan: seededPick(PLANS, rnd),
      dataTotal,
      surplusFlowbyte: Math.min(surplus, dataTotal),
      effectiveDate,
    };
  }

  function generateRandomMockData({
    seed = 12345,
    japanActive = 8,
    abroadActive = 5,
    usedCount = 10,
  } = {}) {
    const rnd = mulberry32(seed);

    const japanCapacityList = Array.from({ length: japanActive }, () =>
      createEntry(rnd, STATUS.INUSE)
    );

    const overseasCapacityList = Array.from({ length: abroadActive }, () =>
      createEntry(rnd, STATUS.INUSE)
    ).map((e) => ({ ...e, countryName: "海外" }));

    const usedStatusPool = NON_ACTIVE; // ["消費済み","待機中","有効期限切れ"]
    const japanCapacityUsedList = Array.from({ length: usedCount }, () =>
      createEntry(rnd, seededPick(usedStatusPool, rnd))
    );

    return { japanCapacityList, overseasCapacityList, japanCapacityUsedList };
  }

  // which item becomes ACTIVE per region (tweak freely) nearest to expire or vice versa
  const SELECT_MODE = { japan: "nearest", abroad: "nearest" };
  // e.g. use "latest" for both: { japan: "latest", abroad: "latest" }

  let activeRegion = "japan"; // default

  async function detectRegion() {
    // Local override (for easy testing)
    const override =
      new URLSearchParams(location.search).get("region") || // ?region=abroad
      localStorage.getItem("region") || // saved choice
      window.userRegion; // manual set in console

    if (override) {
      activeRegion = override;
      return activeRegion;
    }

    // Placeholder for API logic later
    // Example:
    // const res = await fetch("/api/user-context");
    // const data = await res.json();
    // activeRegion = data.region ?? "japan";

    return activeRegion;
  }

  const USE_MOCK = true; // Toggle mock mode manually

  async function fetchCapacityData() {
    if (USE_MOCK) {
      // Use dynamic generator put 0 if you want to check no data
      const mockData = generateRandomMockData({
        seed: window.currentDeviceSeed ?? 12345,
        japanActive: 10,
        abroadActive: 15,
        usedCount: 20,
      });

      // Simulate delay like a real API
      await new Promise((resolve) => setTimeout(resolve, 300));
      return mockData;
    }
  }
  // --- END MOCK SETUP ---

  const serialDevices = [];
  
  function setFeaturedLoading(isLoading) {
    const loaderEl = document.getElementById("featured-loader");
    const contentEl = document.getElementById("featured-content");
    if (!loaderEl || !contentEl) return;

    if (isLoading) {
      loaderEl.removeAttribute("hidden");
      loaderEl.style.display = "flex";
      contentEl.setAttribute("aria-busy", "true");
      contentEl.hidden = true;
    } else {
      loaderEl.style.display = "none";
      loaderEl.setAttribute("hidden", "");
      contentEl.removeAttribute("aria-busy");
      contentEl.hidden = false;
    }
  }

  let C_SEP, customerId, hash, baseUrl;
  // Fetch and display devices for the customer
  async function fetchAndRenderDevices() {
    const deviceListContainer = document.getElementById("deviceList");
    const loader = document.getElementById("deviceLoader");
    const circWrap = document.querySelector(
      ".featured-circular-progress-container"
    );
    const scrollWrap = document.querySelector(".scroll-wrapper");
    const cardEl = document.getElementById("infoCard");

    setFeaturedLoading(true);

    loader.style.display = "flex";
    deviceListContainer.style.display = "none";
    circWrap.style.display = "none";
    scrollWrap.style.display = "block";
    cardEl.classList.add("loading");

    if (!customerId || !hash || !baseUrl || !C_SEP) {
      console.warn(
        "Missing config values. Cannot proceed with device fetch. Check AWS setup."
      );
      return;
    }
    try {
      const response = await fetch(`${baseUrl}function-ecoco-device-read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId, hash }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      // Check if tags exist
      const tags = data?.tags || "";
      // If it's a single string of CSV, split. If it's already an array, flatten it
      const parsedSerials =
        typeof tags === "string"
          ? tags.split(",")
          : Array.isArray(tags)
            ? tags
            : [];
      // Clear the existing entries
      serialDevices.length = 0;
      // Store valid entries only
      parsedSerials.forEach((entry) => {
        if (entry.includes(C_SEP)) {
          serialDevices.push(entry);
        }
      });

      // TODO Add timestamp in the future for better sorting
      const sortedDevices = serialDevices.sort((a, b) => {
        const labelA = a.split(C_SEP)[0].toLowerCase();
        const labelB = b.split(C_SEP)[0].toLowerCase();
        return labelA.localeCompare(labelB);
      });

      if (sortedDevices.length > 0) {
        updateFeaturedDevice(sortedDevices[0]);
        renderDeviceDropdownOptions(sortedDevices);
        renderDeviceTable(sortedDevices, deviceListContainer, true);
      } else {
        updateFeaturedDevice(null);
        renderDeviceTable([], deviceListContainer, true);
        renderDeviceDropdownOptions([]);
      }
    } catch (error) {
      console.error("Error fetching serials:", error);
      setFeaturedLoading(false);
    } finally {
      loader.style.display = "none";
      deviceListContainer.style.display = ""; // or "flex"
      circWrap.style.display = "flex";
      scrollWrap.style.display = "block";
      cardEl.classList.remove("loading");
      deviceListContainer.classList.add("fade-in");
      circWrap.classList.add("fade-in");
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    const ecocoCustomerId = document.getElementById("ecoco-customerId");
    const ecocoCustomerIdHash = document.getElementById("ecoco-customerIdHash");
    const ecocoBaseUrl = document.getElementById("ecoco-baseUrl");
    const ecocoCsep = document.getElementById("ecoco-C_SEP");

    if (ecocoCustomerId) {
      customerId = ecocoCustomerId.dataset.value;
    }

    if (ecocoCustomerIdHash) {
      hash = ecocoCustomerIdHash.dataset.value;
    }

    if (ecocoBaseUrl) {
      baseUrl = ecocoBaseUrl.dataset.value;
    }

    if (ecocoCsep) {
      C_SEP = ecocoCsep.dataset.value;
    }

    fetchAndRenderDevices(); // Call only after config is set

    document.addEventListener("click", async (e) => {
      const btn = e.target.closest(".anchor-btn");
      if (!btn) return;

      e.preventDefault();
      // If mobile & not showing terminal while focused, switch
      const { mobileMode, activeTab } = window.getMobileState
        ? window.getMobileState()
        : { mobileMode: "all", activeTab: "terminal" };

      if (
        window.innerWidth < 768 &&
        mobileMode === "focused" &&
        activeTab !== "terminal"
      ) {
        window.setMobileMode?.("focused", "terminal");
        // layout paint
        await new Promise((r) =>
          requestAnimationFrame(() => requestAnimationFrame(r))
        );
      }

      const card = btn.closest(".device-card");
      const label = card
        ?.querySelector(".device-label")
        ?.getAttribute("data-full");
      const serial = card?.querySelector(".device-serial")?.textContent?.trim();

      // per-device seed
      if (serial) {
        if (!deviceSeeds.has(serial)) deviceSeeds.set(serial, hashSeed(serial));
        window.currentDeviceSeed = deviceSeeds.get(serial);
      }

      // Update featured (this triggers async render)
      if (label && serial) setFavoriteDevice(label, serial);

      // Wait for render to finish (reads plan/expiry if event delivers it)
      const { plan, expiry } = await new Promise((resolve) => {
        let settled = false;
        const onUpdate = (evt) => {
          if (settled) return;
          settled = true;
          document.removeEventListener("featured:updated", onUpdate);
          clearTimeout(t);
          resolve(evt?.detail || {});
        };
        const t = setTimeout(() => onUpdate({ detail: {} }), 250);
        document.addEventListener("featured:updated", onUpdate, { once: true });
      });

      // Optional: if you want to force overlay texts here as backup:
      if (plan || expiry) {
        const planEl = document.querySelector(".plan-name span:last-child");
        const expiryEl = document.querySelector(".expiry-text span:last-child");
        if (planEl && plan) planEl.textContent = plan;
        if (expiryEl && expiry) expiryEl.textContent = expiry;
      }

      // Scroll page so the whole current-device-container is in view
      const pageScroller =
        document.scrollingElement || document.documentElement;
      const container = document.querySelector(".current-device-container");
      const header = document.querySelector(".site-header, .sticky-header");
      const pageOffset = (header?.offsetHeight || 0) + 60;

      if (container) {
        const top =
          container.getBoundingClientRect().top +
          pageScroller.scrollTop -
          pageOffset;
        pageScroller.scrollTo({ top, behavior: "smooth" });
      }
      window.smoothScrollToSection("terminal", 60);
      // Then scroll inner pane so featured content is at the top of the right pane
      const inner = document.querySelector(".scroll-wrapper");
      const target = document.getElementById("featured-content");
      if (inner && target) {
        const innerTop =
          target.getBoundingClientRect().top -
          inner.getBoundingClientRect().top +
          inner.scrollTop;
        inner.scrollTo({ top: innerTop, behavior: "smooth" });
      }
    });
  });

  function isNonEnglish(text) {
    return /[^a-zA-Z0-9\s.,!?'"()\-]/.test(text);
    // return /[^\p{L}\p{N}\p{P}\p{Z}]/u.test(text);
  }

  window.setFavoriteDevice = function (label, serial) {
    const container = document.querySelector(
      ".featured-circular-progress-container"
    );
    const circle = document.querySelector(".featured-circular-progress");

    if (!container || !serial || !label) {
      console.warn("Missing container, serial, or label");
      return;
    }

    container.classList.remove("fade-in");

    // Toggle .active class for all cards
    document
      .querySelectorAll(".device-card")
      .forEach((el) => el.classList.remove("active"));

    const allCards = document.querySelectorAll(".device-card");
    allCards.forEach((card) => {
      const cLabel = card
        .querySelector(".device-label")
        ?.getAttribute("data-full");
      const cSerial = card.querySelector(".device-serial")?.textContent.trim();
      if (cLabel === label && cSerial === serial) {
        card.classList.add("active");
      }
    });

    const featuredContainer = document.querySelector(
      ".device-registration-container"
    );

    // Animate the container
    featuredContainer.classList.remove("fade-highlight");
    void featuredContainer.offsetWidth;
    featuredContainer.classList.add("fade-highlight");

    window._forceNextTabKey = "japan";
    // Update featured device info
    updateFeaturedDevice(`${label}${C_SEP}${serial}`);

    const selectEl = document.getElementById("device-select");
    if (selectEl) {
      const options = Array.from(selectEl.options);
      const matched = options.find((opt) => opt.value === serial);
      if (matched) {
        selectEl.value = serial;
      } else {
        selectEl.selectedIndex = 0; // fallback to default if unmatched
      }
    }
  };

  function renderDeviceTable(serialList, container, replace = true) {
    const tempContainer = document.createElement("div"); // Hidden temp container

    if (!serialList || serialList.length === 0) {
      tempContainer.innerHTML = `
        <div class="no-devices-message fade-in">
          <p style="text-align: center;">登録されたデバイスがありません。</p>
          <p>「端末登録」ボタンからデバイスを追加してください。</p>
        </div>
      `;

      if (replace) {
        container.innerHTML = tempContainer.innerHTML;
      } else {
        container.append(tempContainer);
      }

      return;
    }

    serialList.forEach((entry, i) => {
      const [label, serial] = entry.split(C_SEP);
      const okawariIdDesktop = `okawari-btn-${i}-desk`;
      const okawariIdFooter = `okawari-btn-${i}-foot`;
      const payload = `${label}${C_SEP}${serial}`;
      const deviceId = `deviceListLabel-${i}`;
      let truncLabel;
      if (isNonEnglish(label)) {
        truncLabel = label.length > 7 ? label.slice(0, 7) + "..." : label;
      } else {
        truncLabel = label.length > 10 ? label.slice(0, 10) + "..." : label;
      }

      const deviceHTML = `
        <div class="devices fade-in">
          <div class="device-card">

            <div class="device-header">
            
              <div class="device-actions">
                <span onclick='openModal("register-device", ${JSON.stringify({
                  isEdit: true,
                  label,
                  serial,
                })})' >
                <i class="fas fa-pen edit-icon"></i>
                </span>
                
                <span onclick="openModal('delete-device', '${payload}')" >
                <i class="fas fa-trash delete-icon"></i>
                </span>
              </div>
            </div>
            
            <div class="device-list-label-wrapper">
              <p class="device-title">ラベル</p>
              <h3 data-full="${label}" class="device-label" id="${deviceId}">
              ${truncLabel || "ラベルなし"}
              </h3>
            </div>
            
            <div class="device-list-serial-wrapper">
              <p class="device-title">シリアル番号</p>
              <p class="device-serial">${serial}</p>
            </div>

            <div class="device-actions desktop-only">
              <a  href="#" class="custom-btn no-margin anchor-btn">
                <span>残量確認</span>
                <span class="nav-icon"><i class="fa-solid fa-angle-right"></i></span>
              </a>
              <a  href="#" class="custom-btn no-margin" id="${okawariIdDesktop}" onClick='event.preventDefault(); openOkawariDrawer("${label}", "${serial}")'>
                <span>おかわり</span>
                <span class="nav-icon"><i class="fa-solid fa-angle-right"></i></span>
              </a>
              <span onclick='openModal("register-device", ${JSON.stringify({
                isEdit: true,
                label,
                serial,
              })})' 
              >
                <i class="fas fa-pen edit-icon"></i>
              </span>
              
              <span onclick="openModal('delete-device', '${payload}')" >
                <i class="fas fa-trash delete-icon"></i>
              </span>
            </div>
              
            <div class="device-footer">
              <a  href="#" class="custom-btn no-margin anchor-btn">
                <span>残量確認</span>
                <span class="nav-icon"><i class="fa-solid fa-angle-right"></i></span>
              </a>
  
              <a  href="#" class="custom-btn no-margin" id="${okawariIdFooter}" onClick='event.preventDefault(); openOkawariDrawer("${label}", "${serial}")'>
                <span>おかわり</span>
                <span class="nav-icon"><i class="fa-solid fa-angle-right"></i></span>
              </a>
            </div>

          </div>
        </div>
      `;
      tempContainer.insertAdjacentHTML("beforeend", deviceHTML);
    });
    if (replace) {
      container.innerHTML = tempContainer.innerHTML;
    } else {
      container.append(tempContainer);
    }

    container.querySelectorAll(".device-label").forEach((labelEl) => {
      const full = labelEl.getAttribute("data-full");
      const visibleText = labelEl.textContent.trim();

      if (full && full !== visibleText) {
        labelEl.classList.add("truncate-tooltip");
        labelEl.style.cursor = "help";

        labelEl.addEventListener("click", () => {
          labelEl.classList.add("tooltip-visible");

          clearTimeout(labelEl._tooltipTimeout); // store timeout per element

          labelEl._tooltipTimeout = setTimeout(() => {
            labelEl.classList.remove("tooltip-visible");
          }, 2000);
        });
      }
    });
  }

  function renderDeviceDropdownOptions(serialList) {
    const selectEl = document.getElementById("device-select");
    const arrow = document.querySelector(".custom-arrow");
    if (!selectEl) return;

    // First, clear all options except the first placeholder
    selectEl.innerHTML = "";

    // Add the default placeholder
    const placeholderOption = document.createElement("option");
    placeholderOption.textContent = "--オプションを選択してください--";
    placeholderOption.disabled = true;
    placeholderOption.hidden = true; // Optional: Hide from dropdown but show as selected text
    placeholderOption.selected = true;
    selectEl.appendChild(placeholderOption);

    if (!serialList || serialList.length === 0) return;

    serialList.forEach((entry, i) => {
      const [label, serial] = entry.split(C_SEP);
      const option = document.createElement("option");
      option.value = serial;
      option.textContent = `${label} (${serial})`;
      selectEl.appendChild(option);
    });

    // Auto-select the first actual device (index 0)
    const [defaultLabel, defaultSerial] = serialList[0].split(C_SEP);
    selectEl.value = defaultSerial;

    selectEl.addEventListener("focus", () => {
      arrow.style.transform = "translateY(-50%) rotate(180deg)";
    });

    selectEl.addEventListener("blur", () => {
      arrow.style.transform = "translateY(-50%) rotate(0deg)";
    });

    // OPTIONAL: Bind onChange behavior
    selectEl.addEventListener("change", (e) => {
      const selectedSerial = e.target.value;
      const matchedEntry = serialList.find((item) =>
        item.includes(selectedSerial)
      );
      if (arrow) {
        selectEl.addEventListener("focus", () => {
          arrow.style.transform = "translateY(-50%) rotate(180deg)";
        });
        selectEl.addEventListener("blur", () => {
          arrow.style.transform = "translateY(-50%) rotate(0deg)";
        });
      }
      if (!matchedEntry) return;
      const [label, serial] = matchedEntry.split(C_SEP);
      // Keep seeded data consistent per device (same as anchor flow)
      if (serial) {
        if (!deviceSeeds.has(serial)) deviceSeeds.set(serial, hashSeed(serial));
        window.currentDeviceSeed = deviceSeeds.get(serial);
      }

      // This updates the overlay, table, and circle
      setFavoriteDevice(label, serial);
    });
  }

  function statusClass(status) {
    switch (status) {
      case STATUS.INUSE:
        return "cb-status-active";
      case STATUS.NOTACTIVE:
        return "cb-status-inactive";
      case STATUS.USED:
        return "cb-status-expired";
      case STATUS.EXPIRED:
      default:
        return "cb-status-expired";
    }
  }

  const generateTableRows = (
    dataList,
    selectedItem = null,
    sortKey = null,
    sortDir = "asc",
    tabKey, // "japan" | "abroad" | "used"
    emptyMessage = "データがありません"
  ) => {

    const ALL_COLS = [
      { key: "status",           label: "状態" },
      { key: "countryName",      label: "国名" },
      { key: "plan",             label: "プラン" },
      { key: "dataTotal",        label: "合計容量" },
      { key: "surplusFlowbyte",  label: "残り容量" }, // ← hide on "used"
      { key: "effectiveDate",    label: "有効期限" },
    ];
    
    const cols = tabKey === "used"
      ? ALL_COLS.filter(c => c.key !== "surplusFlowbyte")
      : ALL_COLS;
    
      const sorted = sortList(
      Array.isArray(dataList) ? dataList : [],
      selectedItem,
      sortKey,
      sortDir
    );

    if (sorted.length === 0) {
      return `
      <table class="cb-table">
        <thead class="cb-table-header">
          <tr class="cb-table-row">
            ${cols.map(c => `<th data-sort="${c.key}">${c.label}</th>`).join("")}
          </tr>
        </thead>
        <tbody class="cb-table-body">
          <tr>
            <td colspan="${cols.length}">
              <div class="no-data-wrapper">${emptyMessage}</div>
            </td>
          </tr>
        </tbody>
      </table>`;
    }

    const header = (key, label) => {
      const active = sortKey === key ? ` sorted ${sortDir}` : "";
      const arrow = sortKey === key ? (sortDir === "asc" ? "▲" : "▼") : "";
      return `<th data-sort="${key}" class="${active}">${label} <span class="sort-indicator">${arrow}</span></th>`;
    };

    const rows = sorted
      .map((item) => {
        // only the preselected remains ACTIVE; other actives become 待機中
        const dispStatus = displayStatus(item, selectedItem, tabKey);

        const cells = [
          `<td data-label="状態">
            <div class="cb-plan-label">
              <span class="cb-status-icon ${statusClass(dispStatus)}"></span>
              <span class="cb-status-text">${dispStatus}</span>
            </div>
          </td>`,
          `<td data-label="国名">${item.countryName || "N/A"}</td>`,
          `<td data-label="プラン">${item.plan || "不明"}</td>`,
          `<td data-label="合計容量">${item.dataTotal || "N/A"}GB</td>`,
          // remaining capacity cell only if column present
          ...(cols.some(c => c.key === "surplusFlowbyte")
            ? [`<td data-label="残り容量">${item.surplusFlowbyte || "N/A"}GB</td>`]
            : []),
          `<td data-label="有効期限">${item.effectiveDate || "不明"}</td>`,
        ];

        return `
          <tr class="cb-data-row">
            ${cells.join("")}
          </tr>
        `;
      })
      .join("");

    return `
    <table class="cb-table">
      <thead class="cb-table-header">
        <tr class="cb-table-row">
          ${cols.map(c => header(c.key, c.label)).join("")}
        </tr>
      </thead>
      <tbody class="cb-table-body">
        ${rows}
      </tbody>
    </table>
  `;
  };

  async function addDevice(serial) {
    const cleanedSerial = serial.trim().replace(/,+$/, "").replace(/,/g, C_SEP);
    try {
      const response = await fetch(`${baseUrl}function-ecoco-device-add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId, sn: cleanedSerial, hash }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      await fetchAndRenderDevices(); // Refresh list!
    } catch (error) {
      console.error("Error adding serial:", error);
    }
  }

  async function deleteDevice(payload) {
    const [, serial] = payload.split(C_SEP);

    const entry = serialDevices.find((item) => item === payload);
    if (!entry) {
      console.warn(`No device found matching: ${payload}`);
      return;
    }

    try {
      const response = await fetch(`${baseUrl}function-ecoco-device-delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId, sn: payload, hash }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const card = document.querySelector(
        `.device-card[data-serial="${serial}"]`
      );
      if (card) {
        card.classList.add("fade-out");
        setTimeout(() => card.remove(), 300);
      }

      const idx = serialDevices.indexOf(entry);
      if (idx !== -1) serialDevices.splice(idx, 1);

      if (serialDevices.length > 0) {
        const [label, serial] = serialDevices[0].split(C_SEP);

        fadeTextUpdate(document.getElementById("deviceLabel"), label);
        fadeTextUpdate(document.getElementById("deviceSerial"), serial);

        const percentEl = document.getElementById("devicePercentage");
        const circle = document.querySelector(".circle");

        if (percentEl && circle) {
          percentEl.classList.add("fade-out");
          circle.classList.add("fade-out");

          setTimeout(() => {
            const used = 20; // placeholder — get actual value if available
            const total = 64;
            const percentage = Math.round((used / total) * 100);

            percentEl.textContent = `${percentage}%`;
            circle.style.strokeDashoffset = 100 - percentage;

            percentEl.classList.remove("fade-out");
            circle.classList.remove("fade-out");
          }, 150);
        }
      } else {
        fadeTextUpdate(document.getElementById("deviceLabel"), "—");
        fadeTextUpdate(document.getElementById("deviceSerial"), "—");
        fadeTextUpdate(document.getElementById("devicePercentage"), "—");

        const circle = document.querySelector(".circle");
        if (circle) {
          circle.classList.add("fade-out");
          setTimeout(() => {
            circle.style.strokeDashoffset = 100;
            circle.classList.remove("fade-out");
          }, 150);
        }
      }
      await fetchAndRenderDevices();
    } catch (error) {
      console.error("Error deleting serial:", error);
    }
  }

  function fadeTextUpdate(el, newText) {
    if (!el) return;
    el.classList.add("fade-out");
    setTimeout(() => {
      el.textContent = newText;
      el.classList.remove("fade-out");
    }, 150);
  }

  async function updateDevice(og, nv) {
    try {
      const response = await fetch(`${baseUrl}function-ecoco-device-update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId, hash, from: og, to: nv }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      // Refresh the device list after update
      await fetchAndRenderDevices();
    } catch (error) {
      console.error("Error updating device:", error);
    }
  }

  function animateCircleProgress({ used, max, element }) {
    const circle = element.querySelector(".circle");
    const remainingData = document.getElementById("deviceUsageText");

    const percent = (used / max) * 100;
    const finalOffset = 100 - percent;
    let currentOffset = 100;
    let animationStart = null;

    // cancel any previous loop and create a new token
    stopRingAnimation();
    const myToken = ringAnimCtrl.token;

    function getColorByPercentage(p) {
      if (p < 30) return "#e63946"; // red
      if (p < 80) return "#3498db"; // blue
      return "#2ecc71"; // green
    }

    function animateProgress(timestamp) {
      // bail if a newer animation started (tab switched, etc.)
      if (myToken !== ringAnimCtrl.token) return;

      if (!animationStart) animationStart = timestamp;
      const progress = Math.min((timestamp - animationStart) / 1000, 1);
      currentOffset = 100 - progress * (100 - finalOffset);

      if (circle) {
        circle.style.strokeDashoffset = String(currentOffset);
        const currentPercent = 100 - currentOffset;
        circle.style.stroke = getColorByPercentage(currentPercent);
      }

      if (remainingData) {
        const currentUsed = Math.round(progress * used * 100) / 100; // optional: round to 2 decimals
        remainingData.textContent = `${currentUsed.toFixed(2)} / ${max}GB`;
      }

      if (progress < 1) {
        requestAnimationFrame(animateProgress);
      } else {
        ringAnimCtrl.rafId = null;
      }
    }

    ringAnimCtrl.rafId = requestAnimationFrame(animateProgress);
  }

  function triggerFade(selector, cls = "fade-in") {
    const el = document.querySelector(selector);
    if (!el) return;
    el.classList.remove(cls);
    // force reflow so the class re-applies
    void el.offsetWidth;
    el.classList.add(cls);
  }

  // Always wire tabs + set initial active tab with provided lists
  function wireTabs(lists) {
    // Figure current active or default
    const currentActiveBtn = document.querySelector(".tab-btn.active");
    const domActiveKey = currentActiveBtn?.dataset.tab || "japan";
    const initialTabKey =
      window._forceNextTabKey || activeRegion || domActiveKey;

    // One-shot override used elsewhere
    delete window._forceNextTabKey;

    // Remove old listeners (clone trick)
    document.querySelectorAll(".tab-btn").forEach((t) => {
      const clone = t.cloneNode(true);
      t.parentNode.replaceChild(clone, t);
    });

    // Bind fresh listeners
    document.querySelectorAll(".tab-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        setActiveTab(btn.dataset.tab, lists);
      });
    });

    // Activate the initial tab right away
    setActiveTab(initialTabKey, lists);
  }

  async function updateFeaturedDevice(entry) {
    setFeaturedLoading(true);
    try {
      const content = document.getElementById("featured-content");
      const labelEl = document.getElementById("deviceLabel");
      const serialEl = document.getElementById("deviceSerial");
      const percentEl = document.getElementById("devicePercentage");
      const circle = document.querySelector(".circle");

      if (!entry || !entry.includes(C_SEP)) {
        console.log("fallback state: no entry or invalid format");
        // Fallback state: no device
        const fallbackTable = generateTableRows([]);

        content.innerHTML = `
          <h2 class="drawer-title">現在の残量</h2>
          <div class="tab-content" id="tab-japan">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
          <div class="tab-content hidden" id="tab-abroad">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
          <div class="tab-content hidden" id="tab-used">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
        `;

        // Reset featured device labels
        labelEl.textContent = "—";
        serialEl.textContent = "—";
        if (percentEl) percentEl.textContent = "—";
        if (circle) circle.style.strokeDashoffset = 100;

        const usageTextEl = document.getElementById("deviceUsageText");
        if (usageTextEl) {
          usageTextEl.textContent = "—";
        }

        wireTabs({
          japan: [],
          abroad: [],
          used: [],
          _selectedJapan: null,
          _selectedAbroad: null,
        });

        // Optional: fire the same event shape you use later
        document.dispatchEvent(
          new CustomEvent("featured:updated", {
            detail: { plan: "—", expiry: "—" },
          })
        );

        return;
      }

      const [label, serial] = entry.split(C_SEP);
      let truncLabel;
      if (isNonEnglish(label)) {
        truncLabel =
          label.length > 3 ? label.slice(0, 3).trimEnd() + "..." : label;
      } else {
        truncLabel =
          label.length > 5 ? label.slice(0, 5).trimEnd() + "..." : label;
      }

      labelEl.textContent = truncLabel || "—";
      serialEl.textContent = serial || "—";

      let japanTableRows = "";
      let overseasTableRows = "";
      let usedTableRows = "";

      // Decide dev/prod by presence/value of #capacity-api-url
      const apiEl = document.getElementById("capacity-api-url");
      const apiUrl = apiEl?.dataset?.value || "";
      const useMock = !apiUrl || apiUrl.includes("dev-api-url"); // treat placeholder as mock

      const result = useMock
        ? await fetchCapacityData()
        : await window.fetchCapacity(serial, apiUrl);

      if (result === null || !result || result.errorMessage) {
        // Fallback state: show 'データがありません' for all tabs and reset SVG ring
        const fallbackTable = generateTableRows(
          [],
          null,
          null,
          "asc",
          undefined,
          result?.errorMessage
        );
        content.innerHTML = `
          <h2 class="drawer-title">現在の残量</h2>
          <div class="tab-content" id="tab-japan">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
          <div class="tab-content hidden" id="tab-abroad">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
          <div class="tab-content hidden" id="tab-used">
            <div class="cb-table-container fade-in">
              ${fallbackTable}
            </div>
          </div>
        `;
        // Reset SVG ring and usage text
        const circle = document.querySelector(".circle");
        if (circle) circle.style.strokeDashoffset = 100;
        const usageTextEl = document.getElementById("deviceUsageText");
        if (usageTextEl) usageTextEl.textContent = "—";

        wireTabs({
          japan: [],
          abroad: [],
          used: [],
          _selectedJapan: null,
          _selectedAbroad: null,
        });

        document.dispatchEvent(
          new CustomEvent("featured:updated", {
            detail: { plan: "—", expiry: "—" },
          })
        );
        return;
      }

      const japanList = Array.isArray(result.japanCapacityList)
        ? result.japanCapacityList
        : [];
      const overseasList = Array.isArray(result.overseasCapacityList)
        ? result.overseasCapacityList
        : [];
      const usedList = Array.isArray(result.japanCapacityUsedList)
        ? result.japanCapacityUsedList
        : [];

      // pick “the one” for each tab
      const selectedJapan = pickByExpiry(japanList, SELECT_MODE.japan) || null;
      const selectedAbroad =
        pickByExpiry(overseasList, SELECT_MODE.abroad) || null;

      // Detect region (stub now; API later)
      activeRegion = await detectRegion();

      // Build tables using current sort state
      japanTableRows = generateTableRows(
        japanList,
        selectedJapan,
        sortState.japan.key,
        sortState.japan.dir,
        "japan"
      );
      overseasTableRows = generateTableRows(
        overseasList,
        selectedAbroad,
        sortState.abroad.key,
        sortState.abroad.dir,
        "abroad"
      );
      usedTableRows = generateTableRows(
        usedList,
        null,
        sortState.used.key,
        sortState.used.dir,
        "used"
      );

      // add fade-in effect in the cb-table-container div if needed
      content.innerHTML = `
        <h2 class="drawer-title">現在の残量</h2>

        <div class="tab-content" id="tab-japan">
          <div class="cb-table-toolbar">
            <label class="sr-only" for="sort-select-japan">並び替え</label>
            <div class="sort-select-wrap">
              <select id="sort-select-japan" class="sort-select" data-tab="japan"></select>
              <span class="custom-arrow sort-arrow" aria-hidden="true"></span>
            </div>
          </div>

          <div class="cb-table-container">
            ${japanTableRows}
          </div>
        </div>

        <div class="tab-content hidden" id="tab-abroad">
          <div class="cb-table-toolbar">
            <label class="sr-only" for="sort-select-abroad">並び替え</label>
            <div class="sort-select-wrap">
              <select id="sort-select-abroad" class="sort-select" data-tab="abroad"></select>
              <span class="custom-arrow sort-arrow" aria-hidden="true"></span>
            </div>
          </div>
          <div class="cb-table-container">
            ${overseasTableRows}
          </div>
        </div>

        <div class="tab-content hidden" id="tab-used">
          <div class="cb-table-toolbar">
            <label class="sr-only" for="sort-select-used">並び替え</label>
            <div class="sort-select-wrap">
              <select id="sort-select-used" class="sort-select" data-tab="used"></select>
              <span class="custom-arrow sort-arrow" aria-hidden="true"></span>
            </div>
          </div>
          <div class="cb-table-container">
            ${usedTableRows}
          </div>
        </div>
      `;

      function populateSortSelect(tabKey) {
        const select = document.getElementById(`sort-select-${tabKey}`);

        if (!select) return;

        // build options
        const opts = SORT_OPTIONS.filter(opt =>
          !(tabKey === "used" && opt.key === "surplusFlowbyte")
        );
        select.innerHTML = opts.map((opt) => {
          const val = optionValue(opt.key, opt.dir);
          // preserve current selection
          const isSelected =
            sortState[tabKey].key === opt.key &&
            sortState[tabKey].dir === opt.dir;
          return `<option value="${val}" ${isSelected ? "selected" : ""}>${
            opt.label
          }</option>`;
        }).join("");
      }

      function bindSortSelect(tabKey, list, selected) {
        const box = document.querySelector(`#tab-${tabKey} .cb-table-container`);
        const select = document.getElementById(`sort-select-${tabKey}`);
        const arrow = document.querySelector(
          `#tab-${tabKey} .sort-select-wrap .sort-arrow`
        );
        if (!box || !select) return;

        // initial population
        populateSortSelect(tabKey);

        // rotate arrow
        const rotateOpen = () => {
          if (arrow) arrow.style.transform = "translateY(-50%) rotate(180deg)";
        };
        const rotateClose = () => {
          if (arrow) arrow.style.transform = "translateY(-50%) rotate(0deg)";
        };

        select.addEventListener("focus", rotateOpen);
        select.addEventListener("blur", rotateClose);
        select.addEventListener("mousedown", rotateOpen);

        // react to changes
        select.addEventListener("change", () => {
          const { key, dir } = parseOptionValue(select.value);
          sortState[tabKey].key = key;
          sortState[tabKey].dir = dir || "asc";
          const html = generateTableRows(
            list,
            selected,
            sortState[tabKey].key,
            sortState[tabKey].dir,
            tabKey
          );
          box.innerHTML = html;
        });
      }

      // Bind for all tabs, using the lists & preselected items you already computed
      bindSortSelect("japan", japanList, selectedJapan);
      bindSortSelect("abroad", overseasList, selectedAbroad);
      bindSortSelect("used", usedList, null);

      function bindSortHandlers(tabKey, list, selected) {
        const box = document.querySelector(`#tab-${tabKey} .cb-table-container`);
        if (!box) return;

        // delegate: works even after re-render
        box.addEventListener("click", (e) => {
          const th = e.target.closest("th[data-sort]");
          if (!th) return;

          const key = th.getAttribute("data-sort");
          const state = sortState[tabKey];

          // toggle dir if same key; else default to asc
          state.dir =
            state.key === key ? (state.dir === "asc" ? "desc" : "asc") : "asc";
          state.key = key;

          // re-render just this table
          const html = generateTableRows(
            list,
            selected,
            state.key,
            state.dir,
            tabKey
          );
          box.innerHTML = html;
        });
      }

      // bind for all tabs
      bindSortHandlers("japan", japanList, selectedJapan);
      bindSortHandlers("abroad", overseasList, selectedAbroad);
      bindSortHandlers("used", usedList, null);

      // unified tab control + overlay sync
      const lists = {
        japan: japanList,
        abroad: overseasList,
        used: usedList,
        _selectedJapan: selectedJapan,
        _selectedAbroad: selectedAbroad,
      };

      const currentActiveBtn = document.querySelector(".tab-btn.active");
      const domActiveKey = currentActiveBtn?.dataset.tab || "japan";
      const initialTabKey =
        window._forceNextTabKey || activeRegion || domActiveKey; // prefer region

      wireTabs(lists);

      const isUsed = initialTabKey === "used";
      const selectedForEvent = isUsed
        ? null
        : initialTabKey === "japan"
          ? selectedJapan
          : selectedAbroad;

      // Truncate tooltip setup
      const el = document.querySelector(".truncate-tooltip");
      if (el && truncLabel.length > 5) {
        el.setAttribute("data-full", label);
        el.style.cursor = "help";

        let tooltipTimeout;
        el.addEventListener("click", () => {
          if (!el.hasAttribute("data-full")) return;
          el.classList.add("tooltip-visible");
          clearTimeout(tooltipTimeout);
          tooltipTimeout = setTimeout(() => {
            el.style.cursor = "default";
            el.classList.remove("tooltip-visible");
          }, 2000);
        });
      } else if (el) {
        el.removeAttribute("data-full");
        el.style.cursor = "default";
      }

      document.dispatchEvent(
        new CustomEvent("featured:updated", {
          detail: {
            plan: selectedForEvent?.plan || "—",
            expiry: selectedForEvent?.effectiveDate || "—",
          },
        })
      );
    } finally {
      setFeaturedLoading(false);
    }
  }

  window.deleteDevice = deleteDevice;
  window.addDevice = addDevice;
  window.updateDevice = updateDevice;
})();
